import { motion, useReducedMotion } from 'motion/react';
import React, { useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Maximize2 } from 'lucide-react';
import AIChatInput from './AIChatInput';
import AIChatMessageList from './AIChatMessageList';
import AIChatPanelHeader from './AIChatPanelHeader';
import AIChatPanelShell from './AIChatPanelShell';
import { DEFAULT_PANEL_WIDTH } from './aiChatLayout';
import { useAIChatPanelResize } from './useAIChatPanelResize';
import { useVantiChat } from './useVantiChat';
import VantiScopePicker from './VantiScopePicker';
import { useState } from 'react';

const AIChat = () => {
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const chat = useVantiChat({
    autoLoad: false,
    onAfterNavigate: () => setOpen(false),
  });

  const {
    panelWidth,
    setPanelWidth,
    isResizing,
    onResizeMouseDown,
    maxPanelWidth,
    widePanelWidth,
    isNarrowPanel,
  } = useAIChatPanelResize();

  // Load agents when panel opens
  useEffect(() => {
    if (!open) return;
    chat.loadAgents();
    chat.loadDefaults();
  }, [open]);

  // Forward vanti:ask event → open the panel
  useEffect(() => {
    const handler = (e: any) => {
      const question = e.detail?.question;
      if (question) setOpen(true);
    };
    window.addEventListener('vanti:ask', handler);
    return () => window.removeEventListener('vanti:ask', handler);
  }, []);

  const handleMessageNavigate = useCallback(
    (path) => {
      navigate(path);
      setOpen(false);
    },
    [navigate],
  );

  return (
    <>
      <div className="fixed right-6 bottom-8 z-40 flex flex-col items-center gap-1">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex"
        >
          <Link
            to="/vanti"
            className="flex h-6 w-6 items-center justify-center rounded-full border border-dark-border bg-dark-elevated text-slate-500 shadow transition-all hover:border-brand-purple/40 hover:text-brand-purple-light"
            aria-label="Open Vanti full window"
            title="Open full window"
          >
            <Maximize2 className="h-3 w-3" aria-hidden />
          </Link>
        </motion.div>
        <motion.button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-14 w-14 items-center justify-center bg-transparent p-0 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-vivid-green-cyan/60 focus-visible:ring-offset-2"
          aria-label="Open Vanti chat"
          whileHover={reduceMotion ? undefined : { scale: 1.08 }}
          whileTap={reduceMotion ? undefined : { scale: 0.92 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        >
          <img
            src="/assets/Vanti2.png"
            alt=""
            className="h-14 w-14 object-contain drop-shadow-md"
            aria-hidden
          />
        </motion.button>
      </div>

      {open && (
        <AIChatPanelShell
          reduceMotion={reduceMotion}
          panelWidth={panelWidth}
          maxPanelWidth={maxPanelWidth}
          isResizing={isResizing}
          onResizeMouseDown={onResizeMouseDown}
          onClose={() => setOpen(false)}
        >
          <AIChatPanelHeader
            reduceMotion={reduceMotion}
            isNarrowPanel={isNarrowPanel}
            currentThreadTitle={chat.activeThreadDisplayTitle}
            messagesCount={chat.messages.length}
            onClearChat={chat.clearChat}
            actionMenuRef={chat.actionMenuRef}
            actionMenuOpen={chat.actionMenuOpen}
            onToggleActionMenu={() => chat.setActionMenuOpen((v) => !v)}
            threadsOpen={chat.threadsOpen}
            onToggleThreads={() => {
              chat.setThreadsOpen((v) => !v);
              chat.setMemoryPanelOpen(false);
              chat.setActionMenuOpen(false);
            }}
            memoryPanelOpen={chat.memoryPanelOpen}
            onToggleMemory={() => {
              chat.setMemoryPanelOpen((v) => !v);
              chat.setThreadsOpen(false);
              chat.setActionMenuOpen(false);
            }}
            hasExportableMessages={chat.hasExportableMessages}
            onCopyConversation={chat.handleCopyConversation}
            onExportMarkdown={chat.handleExportMarkdown}
            onExportPdf={chat.handleExportPdf}
            panelWidth={panelWidth}
            widePanelWidth={widePanelWidth}
            onTogglePanelWidth={() =>
              setPanelWidth((w) =>
                w >= widePanelWidth - 10 ? DEFAULT_PANEL_WIDTH : widePanelWidth,
              )
            }
            onClose={() => setOpen(false)}
            selectedAgentId={chat.selectedAgentId}
            onAgentChange={chat.handleAgentChange}
            agents={chat.agents}
            loading={chat.loading}
            navigationContextEnabled={chat.navigationContextEnabled}
            scopeChip={chat.scopeChip}
            scopeOverride={chat.scopeOverride}
            onOpenScopePicker={() => chat.setPickerOpen(true)}
            ragContextEnabled={chat.ragContextEnabled}
            indexHint={chat.indexHint}
          />

          <AIChatMessageList
            listRef={chat.listRef}
            threadsOpen={chat.threadsOpen}
            threads={chat.threads}
            threadsLoading={chat.threadsLoading}
            onCloseThreads={() => chat.setThreadsOpen(false)}
            onSelectThread={chat.handleSelectThread}
            onDeleteThread={chat.handleDeleteThread}
            onCooldown={chat.onCooldown}
            cooldownRemainingMs={chat.cooldownRemainingMs}
            lastSendPayload={chat.lastSendPayload}
            onRetryLast={chat.retryLast}
            loading={chat.loading}
            messages={chat.messages}
            disabledByOrg={chat.disabledByOrg}
            configured={chat.configured}
            streamingMessageId={chat.streamingMessageId}
            streamingStatus={chat.streamingStatus}
            feedbackState={chat.feedbackState}
            copiedResponseId={chat.copiedResponseId}
            reduceMotion={reduceMotion}
            onFeedback={chat.handleFeedback}
            onCopyResponse={chat.handleCopyResponse}
            onAnswer={chat.handleMessageAnswer}
            onScopeSelect={chat.handleMessageScopeSelect}
            onInlineScopeSelect={chat.handleMessageInlineScopeSelect}
            onNavigate={handleMessageNavigate}
            onModeChange={chat.handleModeChange}
            onSetInput={chat.handleSetInputFromAction}
            onFollowUp={chat.handleFollowUp}
            onRetryFailed={chat.handleRetryFailed}
          />

          <AIChatInput
            memoryPanelOpen={chat.memoryPanelOpen}
            onCloseMemory={() => chat.setMemoryPanelOpen(false)}
            input={chat.input}
            inputRef={chat.inputRef}
            onInputChange={(e) => chat.setInput(e.target.value)}
            onKeyDown={chat.handleKeyDown}
            onSend={() => chat.sendOrQueueWithFormat()}
            onStop={chat.handleStop}
            loading={chat.loading}
            streamingMessageId={chat.streamingMessageId}
            onCooldown={chat.onCooldown}
            chatMode={chat.chatMode}
            onModeChange={chat.handleModeChange}
            reduceMotion={reduceMotion}
            queuedQuestion={chat.queuedQuestion}
            slashPickerOpen={chat.slashPickerOpen}
            slashQuery={chat.slashQuery}
            slashSelectedIndex={chat.slashSelectedIndex}
            onSlashSelect={chat.onSlashSelect}
            formatModifier={chat.formatModifier}
            onClearFormatModifier={chat.onClearFormatModifier}
          />
        </AIChatPanelShell>
      )}

      <VantiScopePicker
        open={chat.pickerOpen}
        onClose={() => chat.setPickerOpen(false)}
        onSelect={(item) => chat.pickScope(item)}
      />
    </>
  );
};

export default AIChat;
